import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import { useCallback } from 'react'
import PropTypes from 'prop-types'

import Form from '../../../../components/Form'
import ExportAsCsvReportFormContent from './ExportAsCsvReportFormContent'

import {
  exportCombinedCampaignsDataAsCsvErrorSelector,
  exportCombinedCampaignsDataAsCsvIsLoadingSelector,
  exportCombinedCampaignsDataAsCsvSelector,
  exportCombinedCampaignsDataAsCsvWasLoadedSelector
} from '../../../../modules/selectors/combinedData'
import {
  clearExportCombinedCampaignsDataAsCsv,
  exportCombinedCampaignsDataAsCsv
} from '../../../../modules/actions/combinedData'
import { selectedFacebookAdAccountSelector } from '../../../../modules/selectors/adAccounts'

import { transformValuesToBE } from './formatters'
import { downloadCSVFile } from '../../../../helpers/other'

import { EXPORT_AS_CSV_REPORT } from '../../../../constants/forms'
import { exportAsCsvLevels, getInitialValues } from './fields'
import { customConversionsSelector } from '../../../../modules/selectors/choices'

const ExportAsCsvReportForm = ({ campaignsIds, dateRangeSelector, level }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const customConversionsList = useSelector(customConversionsSelector)
  const successSubmit = useSelector(exportCombinedCampaignsDataAsCsvWasLoadedSelector)
  const csvData = useSelector(exportCombinedCampaignsDataAsCsvSelector)
  const { id: facebookAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  // On campaigns and lineItems pages allow to select other levels
  const showLevelSelection = level === exportAsCsvLevels.campaign || level === exportAsCsvLevels.lineItem

  const savedDateRange = useSelector(dateRangeSelector)

  const onSubmit = useCallback(
    values => {
      const transformedValues = transformValuesToBE({
        values,
        savedDateRange,
        level,
        facebookAdAccountId,
        campaignsIds,
        showLevelSelection,
        customConversionsList
      })

      dispatch(exportCombinedCampaignsDataAsCsv(transformedValues))
    },
    [dispatch, savedDateRange, level, facebookAdAccountId, campaignsIds, showLevelSelection, customConversionsList]
  )

  const formik = useFormik({
    initialValues: getInitialValues(level),
    onSubmit
  })

  const clearExportDataAsCsvHandler = useCallback(() => {
    dispatch(clearExportCombinedCampaignsDataAsCsv())
  }, [dispatch])

  const onSuccessSubmit = useCallback(() => {
    downloadCSVFile({ data: csvData, fileName: 'combinedData' })
  }, [csvData])

  return (
    <Form
      formName={EXPORT_AS_CSV_REPORT}
      formik={formik}
      errorSelector={exportCombinedCampaignsDataAsCsvErrorSelector}
      isLoadingSelector={exportCombinedCampaignsDataAsCsvIsLoadingSelector}
      successSubmit={successSubmit}
      clearHandler={clearExportDataAsCsvHandler}
      submitText={t('Run Report')}
      onSuccessSubmit={onSuccessSubmit}
    >
      <ExportAsCsvReportFormContent formik={formik} level={level} showLevelSelection={showLevelSelection} />
    </Form>
  )
}

ExportAsCsvReportForm.propTypes = {
  campaignsIds: PropTypes.array.isRequired,
  dateRangeSelector: PropTypes.func.isRequired,
  level: PropTypes.string.isRequired
}

export default ExportAsCsvReportForm
